/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { useAuth } from "@/lib/auth";
import { Plus, Search, Trash2, Mail, Phone as PhoneIcon, Building2 } from "lucide-react";
import Modal from "@/components/Modal";
import ImportExportButtons from "@/components/ImportExportButtons";
import ColumnChooser from "@/components/ColumnChooser";
import { useColumnPrefs } from "@/hooks/useColumnPrefs";

export default function ContactsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = ["admin", "user", "admin_reseller"].includes(user.role);
  const canDelete = ["admin", "user", "admin_reseller"].includes(user.role);

  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, co] = await Promise.all([api.get("/contacts"), api.get("/companies")]);
      setContacts(c.data);
      setCompanies(co.data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const companyMap = useMemo(() => {
    const m = {};
    companies.forEach((c) => { m[c.id] = c; });
    return m;
  }, [companies]);

  const filtered = useMemo(() => {
    const term = q.toLowerCase();
    return contacts.filter((c) => {
      if (filterCompany && c.Company_ID !== filterCompany) return false;
      if (!term) return true;
      const co = companyMap[c.Company_ID];
      return (
        (c.First_Name || "").toLowerCase().includes(term) ||
        (c.Last_Name || "").toLowerCase().includes(term) ||
        (c.Email || "").toLowerCase().includes(term) ||
        (co?.Company_Name || "").toLowerCase().includes(term)
      );
    });
  }, [contacts, companyMap, q, filterCompany]);

  // Build export rows with denormalised Company_Name for round-tripping
  const exportRows = useMemo(() =>
    filtered.map((c) => ({
      First_Name: c.First_Name,
      Last_Name: c.Last_Name,
      Email: c.Email,
      Phone: c.Phone,
      MobileNumber: c.MobileNumber,
      DDINumber: c.DDINumber,
      Job_Title: c.Job_Title,
      Company_Name: companyMap[c.Company_ID]?.Company_Name || "",
    })), [filtered, companyMap]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try {
      await api.delete(`/contacts/${id}`);
      setContacts((cs) => cs.filter((c) => c.id !== id));
      toast.success("Contact deleted.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const allColumns = [
    {
      key: "Name", label: "Name",
      cell: (c) => <div className="font-semibold text-zinc-900">{c.First_Name} {c.Last_Name}</div>,
    },
    { key: "Job_Title", label: "Job Title", cell: (c) => <span className="text-sm text-zinc-600">{c.Job_Title || "—"}</span> },
    {
      key: "Company", label: "Company",
      cell: (c) => {
        const co = companyMap[c.Company_ID];
        return co ? (
          <button
            onClick={() => navigate(`/companies/${co.id}`)}
            className="text-sm text-brand-700 hover:underline inline-flex items-center gap-1"
          >
            <Building2 className="w-3.5 h-3.5" />{co.Company_Name}
          </button>
        ) : <span className="text-sm text-zinc-400">—</span>;
      },
    },
    {
      key: "Email", label: "Email",
      cell: (c) => c.Email ? (
        <a href={`mailto:${c.Email}`} className="text-sm text-zinc-700 hover:text-brand-700 inline-flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" />{c.Email}
        </a>
      ) : <span className="text-sm text-zinc-400">—</span>,
    },
    {
      key: "Phone", label: "Phone",
      cell: (c) => c.Phone ? (
        <a href={`tel:${c.Phone}`} className="text-sm text-zinc-700 inline-flex items-center gap-1.5">
          <PhoneIcon className="w-3.5 h-3.5" />{c.Phone}
        </a>
      ) : <span className="text-sm text-zinc-400">—</span>,
    },
    { key: "MobileNumber", label: "Mobile", defaultVisible: false, cell: (c) => <span className="text-sm">{c.MobileNumber || "—"}</span> },
    { key: "DDINumber", label: "DDI", defaultVisible: false, cell: (c) => <span className="text-sm font-mono">{c.DDINumber || "—"}</span> },
    ...(canDelete ? [{
      key: "_actions", label: "Actions", locked: true, align: "right",
      cell: (c) => (
        <button onClick={() => onDelete(c.id)} className="text-zinc-400 hover:text-red-600 p-1" title="Delete">
          <Trash2 className="w-4 h-4" />
        </button>
      ),
    }] : []),
  ];

  const { visibleColumns, orderedColumns, isVisible, toggleColumn, moveColumn, reset } =
    useColumnPrefs("contacts", allColumns);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Contacts</h1>
          <p className="text-sm text-zinc-500 mt-1">{filtered.length} of {contacts.length} contacts shown.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <ColumnChooser
            tableLabel="contacts"
            orderedColumns={orderedColumns}
            isVisible={isVisible}
            toggleColumn={toggleColumn}
            moveColumn={moveColumn}
            reset={reset}
          />
          {canManage && (
            <>
              <ImportExportButtons
                entity="contacts"
                rows={exportRows}
                exportColumns={["First_Name", "Last_Name", "Email", "Phone", "MobileNumber", "DDINumber", "Job_Title", "Company_Name"]}
                exportName={`edenstone-contacts-${new Date().toISOString().slice(0, 10)}`}
                onImported={load}
              />
              <button data-testid="contacts-new-button" onClick={() => setShowNew(true)} className="es-btn-primary">
                <Plus className="w-4 h-4" /> New contact
              </button>
            </>
          )}
        </div>
      </div>

      <div className="es-card p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="contacts-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email or company…"
            className="es-input pl-9"
          />
        </div>
        <select
          data-testid="contacts-filter-company"
          value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)}
          className="es-select min-w-[200px]"
        >
          <option value="">All companies</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.Company_Name}</option>)}
        </select>
      </div>

      {err && <div className="es-card p-4 text-sm text-red-700">{err}</div>}

      <div className="es-card overflow-x-auto">
        <table className="es-table">
          <thead>
            <tr>
              {visibleColumns.map((c) => (
                <th key={c.key} className={[c.align === "right" ? "text-right" : "", c.width || ""].join(" ")}>
                  {c.header ? c.header() : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={visibleColumns.length} className="text-center py-10 text-sm text-zinc-500">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={visibleColumns.length} className="text-center py-10 text-sm text-zinc-500">No contacts match your filters.</td></tr>}
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`contact-row-${c.id}`}>
                {visibleColumns.map((col) => (
                  <td key={col.key} className={col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : ""}>{col.cell(c)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <NewContactModal companies={companies} onClose={() => setShowNew(false)} onCreated={(c) => { setContacts((prev) => [c, ...prev]); setShowNew(false); }} />}
    </div>
  );
}

export function NewContactModal({ companies, onClose, onCreated }) {
  const [form, setForm] = useState({
    First_Name: "", Last_Name: "", Email: "", Phone: "",
    MobileNumber: "", Job_Title: "", Company_ID: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      const { data } = await api.post("/contacts", form);
      onCreated(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal title="New Contact" onClose={onClose} size="lg">
      <form onSubmit={onSubmit} className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="es-label">First Name *</label>
          <input data-testid="contact-First_Name" required value={form.First_Name} onChange={(e) => u("First_Name", e.target.value)} className="es-input" />
        </div>
        <div>
          <label className="es-label">Last Name *</label>
          <input data-testid="contact-Last_Name" required value={form.Last_Name} onChange={(e) => u("Last_Name", e.target.value)} className="es-input" />
        </div>
        <div className="sm:col-span-2">
          <label className="es-label">Email *</label>
          <input data-testid="contact-Email" type="email" required value={form.Email} onChange={(e) => u("Email", e.target.value)} className="es-input" />
        </div>
        <div className="sm:col-span-2">
          <label className="es-label">Company *</label>
          <select data-testid="contact-Company_ID" required value={form.Company_ID} onChange={(e) => u("Company_ID", e.target.value)} className="es-select w-full">
            <option value="">Select…</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.Company_Name}</option>)}
          </select>
        </div>
        <div>
          <label className="es-label">Phone</label>
          <input value={form.Phone} onChange={(e) => u("Phone", e.target.value)} className="es-input" />
        </div>
        <div>
          <label className="es-label">Mobile</label>
          <input value={form.MobileNumber} onChange={(e) => u("MobileNumber", e.target.value)} className="es-input" />
        </div>
        <div className="sm:col-span-2">
          <label className="es-label">Job Title</label>
          <input value={form.Job_Title} onChange={(e) => u("Job_Title", e.target.value)} className="es-input" />
        </div>

        {err && <div className="sm:col-span-2 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}

        <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="es-btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} data-testid="contact-save" className="es-btn-primary">{saving ? "Saving…" : "Create contact"}</button>
        </div>
      </form>
    </Modal>
  );
}
